<template>
  <div id="dashboard">
    <div class="d-flex align-center flex-wrap mb-4">
      <h1 class="text-h6 font-weight-semibold text--primary me-4">
        Главная
      </h1>

      <v-spacer></v-spacer>

      <span
        v-if="updatedAt"
        class="text-body-2 text--secondary me-4"
      >
        Обновлено в {{ updatedAt }}
      </span>

      <v-btn
        outlined
        small
        :loading="loading"
        @click="refresh"
      >
        <v-icon
          size="18"
          class="me-2"
        >
          {{ icons.mdiRefresh }}
        </v-icon>
        <span>Обновить</span>
      </v-btn>
    </div>

    <v-card v-if="failed">
      <v-card-text class="d-flex flex-column align-center text-center py-12">
        <v-icon
          size="48"
          color="error"
          class="mb-4"
        >
          {{ icons.mdiAlertCircleOutline }}
        </v-icon>
        <p class="text-h6 text--primary mb-1">
          Сводка не загрузилась
        </p>
        <p class="text--secondary mb-6">
          {{ error }}
        </p>
        <v-btn
          color="primary"
          :loading="loading"
          @click="refresh"
        >
          Повторить
        </v-btn>
      </v-card-text>
    </v-card>

    <template v-else>
      <v-alert
        v-if="error"
        type="warning"
        dense
        text
      >
        Обновить сводку не удалось: {{ error }}. {{ staleText }}
      </v-alert>

      <v-row>
        <v-col cols="12">
          <dashboard-today
            :block="blocks.today"
            :loading="firstLoading"
          ></dashboard-today>
        </v-col>

        <v-col cols="12">
          <dashboard-hourly
            :block="blocks.hourly"
            :loading="firstLoading"
          ></dashboard-hourly>
        </v-col>

        <v-col
          cols="12"
          md="7"
        >
          <dashboard-daily
            :block="blocks.daily"
            :today="today"
            :loading="firstLoading"
          ></dashboard-daily>
        </v-col>
        <v-col
          cols="12"
          md="5"
        >
          <dashboard-speed
            :block="blocks.speed"
            :loading="firstLoading"
          ></dashboard-speed>
        </v-col>

        <v-col
          cols="12"
          md="5"
        >
          <dashboard-channels
            :block="blocks.channels"
            :loading="firstLoading"
          ></dashboard-channels>
        </v-col>
        <v-col
          cols="12"
          md="7"
        >
          <dashboard-restaurants
            :block="blocks.restaurants"
            :loading="firstLoading"
          ></dashboard-restaurants>
        </v-col>

        <v-col
          cols="12"
          md="5"
        >
          <dashboard-attention
            :block="blocks.attention"
            :loading="firstLoading"
          ></dashboard-attention>
        </v-col>
        <v-col
          cols="12"
          md="7"
        >
          <dashboard-statistics-card></dashboard-statistics-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiAlertCircleOutline, mdiRefresh } from '@mdi/js'
import DashboardToday from '@/views/dashboard/dashboard-today'
import DashboardHourly from '@/views/dashboard/dashboard-hourly'
import DashboardDaily from '@/views/dashboard/dashboard-daily'
import DashboardSpeed from '@/views/dashboard/dashboard-speed'
import DashboardChannels from '@/views/dashboard/dashboard-channels'
import DashboardRestaurants from '@/views/dashboard/dashboard-restaurants'
import DashboardAttention from '@/views/dashboard/dashboard-attention'
import DashboardStatisticsCard from '@/views/dashboard/dashboard-statistics-card'
import { dashboardBlocks, toUpdatedAtString } from '@/helpers/dashboard'

const REFRESH_MS = 5 * 60 * 1000

export default {
  name: 'Dashboard',
  components: {
    DashboardToday,
    DashboardHourly,
    DashboardDaily,
    DashboardSpeed,
    DashboardChannels,
    DashboardRestaurants,
    DashboardAttention,
    DashboardStatisticsCard,
  },
  data: () => ({
    icons: { mdiAlertCircleOutline, mdiRefresh },
  }),
  computed: {
    ...mapState('dashboard', ['data', 'loading', 'error']),
    blocks() {
      return dashboardBlocks(this.data)
    },
    updatedAt() {
      return this.data ? toUpdatedAtString(this.data.generatedAt) : ''
    },

    today() {
      const { today } = this.blocks
      const generatedAt = this.data && typeof this.data.generatedAt === 'string' ? this.data.generatedAt : ''

      return (today && today.date) || generatedAt.slice(0, 10) || null
    },

    firstLoading() {
      return this.loading && !this.data
    },
    failed() {
      return Boolean(this.error) && !this.data
    },
    staleText() {
      return this.updatedAt ? `На экране — данные на ${this.updatedAt}.` : 'На экране — прежние данные.'
    },
  },
  created() {
    document.title = 'Главная'

    this.refresh()
    this.timer = setInterval(this.refreshIfVisible, REFRESH_MS)
    document.addEventListener('visibilitychange', this.refreshIfStale)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.refreshIfStale)
  },
  methods: {
    ...mapActions('dashboard', ['fetch']),
    async refresh() {
      this.refreshedAt = Date.now()
      await this.fetch()
    },

    refreshIfVisible() {
      if (!document.hidden) this.refresh()
    },

    refreshIfStale() {
      if (!document.hidden && Date.now() - this.refreshedAt >= REFRESH_MS) this.refresh()
    },
  },
}
</script>
